import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Switch,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
import { v4 as uuidv4 } from "uuid";

const QUESTION_TYPES = [
  { value: "short_answer", label: "Short Answer" },
  { value: "checkbox_group", label: "Checkbox Group" },
  { value: "custom_checkbox_group", label: "Custom Checkbox Group" },
  // Add additional types as needed
];

function DynamicQuestionGroupModule({
  module,
  onChange,
  onDelete,
  isEditable,
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [conditionalOpen, setConditionalOpen] = useState(false);

  const handleLabelChange = (e) => {
    onChange({ ...module, label: e.target.value });
  };

  const handleRandomiseChange = (e) => {
    const value = parseInt(e.target.value, 10) || 0;
    onChange({ ...module, randomise: value });
  };

  const handleRequiredToggle = (e) => {
    onChange({ ...module, required: e.target.checked });
  };

  const handleAdminWorkflowToggle = (e) => {
    onChange({ ...module, approvalRequired: e.target.checked });
  };

  // Update a question's label or type
  const updateQuestion = (id, field, value) => {
    const updatedQuestions = (module.questions || []).map((q) =>
      q.id === id ? { ...q, [field]: value } : q,
    );
    onChange({ ...module, questions: updatedQuestions });
  };

  const addQuestion = () => {
    const newQuestion = {
      id: uuidv4(),
      label: "New Question",
      type: "short_answer",
      options: [],
      correctResponses: [],
    };
    onChange({
      ...module,
      questions: [...(module.questions || []), newQuestion],
    });
  };

  const removeQuestion = (id) => {
    const updatedQuestions = (module.questions || []).filter(
      (q) => q.id !== id,
    );
    onChange({ ...module, questions: updatedQuestions });
  };

  // --- Option-handling functions for questions of type checkbox_group/custom_checkbox_group ---

  const updateQuestionOption = (questionId, optionIndex, value) => {
    const updatedQuestions = (module.questions || []).map((q) => {
      if (q.id === questionId) {
        const newOptions = [...(q.options || [])];
        newOptions[optionIndex] = value;
        return { ...q, options: newOptions };
      }
      return q;
    });
    onChange({ ...module, questions: updatedQuestions });
  };

  const removeQuestionOption = (questionId, optionIndex) => {
    const updatedQuestions = (module.questions || []).map((q) => {
      if (q.id === questionId) {
        const newOptions = [...(q.options || [])];
        const optionToRemove = newOptions[optionIndex];
        newOptions.splice(optionIndex, 1);
        let newCorrect = q.correctResponses || [];
        if (newCorrect.includes(optionToRemove)) {
          newCorrect = newCorrect.filter((resp) => resp !== optionToRemove);
        }
        return { ...q, options: newOptions, correctResponses: newCorrect };
      }
      return q;
    });
    onChange({ ...module, questions: updatedQuestions });
  };

  const addQuestionOption = (questionId) => {
    const updatedQuestions = (module.questions || []).map((q) => {
      if (q.id === questionId) {
        const newOptions = [
          ...(q.options || []),
          `Option ${q.options ? q.options.length + 1 : 1}`,
        ];
        return { ...q, options: newOptions };
      }
      return q;
    });
    onChange({ ...module, questions: updatedQuestions });
  };

  const toggleQuestionCorrectResponse = (questionId, option, isChecked) => {
    const updatedQuestions = (module.questions || []).map((q) => {
      if (q.id === questionId) {
        let currentCorrect = q.correctResponses ? [...q.correctResponses] : [];
        if (isChecked) {
          if (!currentCorrect.includes(option)) {
            currentCorrect.push(option);
          }
        } else {
          currentCorrect = currentCorrect.filter((resp) => resp !== option);
        }
        return { ...q, correctResponses: currentCorrect };
      }
      return q;
    });
    onChange({ ...module, questions: updatedQuestions });
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
      {/* Module Header */}
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: 1 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          DYNAMIC QUESTION GROUP MODULE
        </Typography>
        {isEditable && (
          <Box>
            <Tooltip title="Settings">
              <IconButton
                onClick={() => setSettingsOpen(!settingsOpen)}
                size="small"
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Conditional Visibility">
              <IconButton
                onClick={() => setConditionalOpen(!conditionalOpen)}
                size="small"
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Module">
              <IconButton onClick={onDelete} size="small" color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Module Preview */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          padding: 2,
          backgroundColor: "#fafafa",
          marginBottom: 1,
        }}
      >
        <Typography variant="subtitle1">
          {module.label || "Dynamic Question Group"}
        </Typography>
        <List>
          {(module.questions || []).map((q) => (
            <ListItem key={q.id}>
              <ListItemText primary={q.label} secondary={`Type: ${q.type}`} />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Settings Panel */}
      {isEditable && settingsOpen && (
        <Box sx={{ marginTop: 2 }}>
          <TextField
            label="Group Label"
            value={module.label || ""}
            onChange={handleLabelChange}
            fullWidth
            margin="dense"
          />

          {/* NEW: Randomise Number Input */}
          <TextField
            label="Randomise Number"
            type="number"
            value={module.randomise || 0}
            onChange={handleRandomiseChange}
            fullWidth
            margin="dense"
            helperText="Number of questions to randomly select (0 for all)"
          />

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addQuestion}
            sx={{ marginTop: 1 }}
          >
            Add Question
          </Button>
          {(module.questions || []).map((q) => (
            <Box
              key={q.id}
              sx={{
                marginTop: 2,
                padding: 1,
                border: "1px solid #ccc",
                borderRadius: 1,
              }}
            >
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={4}>
                  <TextField
                    label="Question Label"
                    value={q.label}
                    onChange={(e) =>
                      updateQuestion(q.id, "label", e.target.value)
                    }
                    fullWidth
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth margin="dense">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={q.type}
                      label="Type"
                      onChange={(e) =>
                        updateQuestion(q.id, "type", e.target.value)
                      }
                    >
                      {QUESTION_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    onClick={() => removeQuestion(q.id)}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>

              {/* If question type supports options, render options editor */}
              {(q.type === "checkbox_group" ||
                q.type === "custom_checkbox_group") && (
                <Box sx={{ ml: 3, mt: 1 }}>
                  <Typography variant="subtitle2">Options:</Typography>
                  {(q.options || []).map((option, optIndex) => (
                    <Grid
                      container
                      spacing={1}
                      alignItems="center"
                      key={optIndex}
                    >
                      <Grid item xs={6}>
                        <TextField
                          label={`Option ${optIndex + 1}`}
                          value={option}
                          onChange={(e) =>
                            updateQuestionOption(q.id, optIndex, e.target.value)
                          }
                          fullWidth
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                q.correctResponses
                                  ? q.correctResponses.includes(option)
                                  : false
                              }
                              onChange={(e) =>
                                toggleQuestionCorrectResponse(
                                  q.id,
                                  option,
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label="Correct"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <IconButton
                          onClick={() => removeQuestionOption(q.id, optIndex)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addQuestionOption(q.id)}
                    size="small"
                    sx={{ mt: 1, ml: 3 }}
                  >
                    Add Option
                  </Button>
                </Box>
              )}
            </Box>
          ))}

          {/* Status Dropdowns */}
          <Grid container spacing={2} sx={{ marginTop: 2 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="editable-status-label">
                  Editable when in Status:
                </InputLabel>
                <Select
                  labelId="editable-status-label"
                  value={module.editableWhenStatus || 0}
                  onChange={(e) =>
                    onChange({ ...module, editableWhenStatus: e.target.value })
                  }
                  label="Editable when in Status:"
                >
                  <MenuItem value={0}>0, valid</MenuItem>
                  <MenuItem value={8}>8, Completed</MenuItem>
                  <MenuItem value={3}>3, Awaiting Review</MenuItem>
                  <MenuItem value={2}>2, Requested</MenuItem>
                  <MenuItem value={9}>9, Closed</MenuItem>
                  <MenuItem value={7}>7, Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="visible-status-label">
                  Visible when in Status:
                </InputLabel>
                <Select
                  labelId="visible-status-label"
                  value={module.visibleWhenStatus || 0}
                  onChange={(e) =>
                    onChange({ ...module, visibleWhenStatus: e.target.value })
                  }
                  label="Visible when in Status:"
                >
                  <MenuItem value={0}>0, valid</MenuItem>
                  <MenuItem value={8}>8, Completed</MenuItem>
                  <MenuItem value={3}>3, Awaiting Review</MenuItem>
                  <MenuItem value={2}>2, Requested</MenuItem>
                  <MenuItem value={9}>9, Closed</MenuItem>
                  <MenuItem value={7}>7, Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" sx={{ marginTop: 2 }}>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={module.required || false}
                    onChange={handleRequiredToggle}
                    color="primary"
                  />
                }
                label="Required"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={module.approvalRequired || false}
                    onChange={handleAdminWorkflowToggle}
                    color="primary"
                  />
                }
                label="Admin Workflow"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {isEditable && conditionalOpen && (
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="body2">
            Conditional Visibility settings...
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default DynamicQuestionGroupModule;
